import { useEffect } from 'react';

/**
 * BackgroundVideoPreloader
 *
 * Presentation-critical videos are large (Mario Intro ~135 MB, Dual-System
 * Explainer ~189 MB). If we wait for the user to arrive at the slide before
 * the <video> element is mounted, the browser only THEN starts fetching the
 * file — leading to a multi-second black screen after the presenter clicks
 * "play".
 *
 * This component fixes the perceived latency by streaming those videos into
 * the browser's HTTP cache as soon as the presentation is opened. The real
 * <video> element on the destination slide then picks up cached bytes and
 * starts playback almost immediately.
 *
 * Strategy:
 *  1. Wait a short beat so we don't compete with the initial JS/font/CSS
 *     paint of the first slide (Hero).
 *  2. Kick off a low-priority fetch() per URL. We stream+discard the body so
 *     it lands in the HTTP cache without eating extra RAM (progressive read
 *     of the ReadableStream, no accumulation).
 *  3. If the connection is Save-Data / 2G we bail out — no point burning
 *     bandwidth we don't have.
 */
export default function BackgroundVideoPreloader({ urls = [], delayMs = 2500 }) {
  useEffect(() => {
    if (!urls || urls.length === 0) return undefined;

    // Offline-Bundle (file://): fetch() auf lokale Dateien ist gesperrt und
    // ein Vorwärmen bringt bei Wiedergabe von Platte nichts — daher überspringen.
    if (process.env.REACT_APP_STANDALONE === 'true') return undefined;

    // Respect Save-Data + very slow connections.
    const nav = (typeof navigator !== 'undefined' && navigator.connection) || null;
    if (nav && (nav.saveData === true || nav.effectiveType === 'slow-2g' || nav.effectiveType === '2g')) {
      return undefined;
    }

    let cancelled = false;
    const controllers = [];

    const kick = async (url) => {
      if (cancelled) return;
      try {
        const ctrl = new AbortController();
        controllers.push(ctrl);
        const res = await fetch(url, {
          method: 'GET',
          credentials: 'omit',
          cache: 'force-cache',
          priority: 'low',
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        // Progressive drain — do not accumulate. Cached by the network layer.
         
        while (true) {
          if (cancelled) { try { reader.cancel(); } catch (_) { /* noop */ } break; }
           
          const { done } = await reader.read();
          if (done) break;
        }
      } catch (_) { /* swallow — best-effort prefetch */ }
    };

    const timer = setTimeout(() => {
      // Kick sequentially so the first slide's video finishes warming before
      // the second one starts — prevents saturating the connection.
      (async () => {
        for (const u of urls) {
          if (cancelled) break;
           
          await kick(u);
        }
      })();
    }, delayMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controllers.forEach((c) => { try { c.abort(); } catch (_) { /* noop */ } });
    };
  }, [urls, delayMs]);

  return null;
}
